import React, { useEffect, useRef, useState } from "react";
import { TemplateSource } from "./workflowTypes";
import {
  apiProblem,
  importTemplate,
  inspectTemplate,
} from "../../api/cafWorkflows";
import { SafeEmailPreview } from "./WorkflowPreview";
import "./TemplateSourceEditor.css";
const SOURCE_PROBLEMS: Record<string, string> = {
  template_source_required: "No source has been imported. Inspect and import the configured template HTML below.",
  template_version_changed: "Source changed. Reload and inspect the latest version before importing again.",
  send_preparation_key_missing: "Protected email storage is unavailable. An administrator must configure the existing shared protection key before importing.",
  send_preparation_key_invalid: "Protected email storage is unavailable. An administrator must check the existing shared protection key.",
  template_source_unavailable: "The stored source cannot be read. Ask an administrator to check protected email storage.",
  template_source_changed: "The stored source failed its integrity check. Ask an administrator to inspect it.",
  unsafe_template_html: "This source contains unsupported or unsafe HTML. Review its metadata, links and dynamic content before importing.",
  unsupported_template_syntax: "This source uses unsupported template syntax. Review the source before importing.",
  invalid_template_source: "Import complete UTF-8 HTML no larger than 2 MiB.",
};
const sourceProblem = (code?: string) => (code && SOURCE_PROBLEMS[code]) || "Could not load or import the source. Reload and try again.";

export default function TemplateSourceEditor({
  templateId,
  onImported,
  onBusyChange,
  disabled = false,
}: {
  templateId: string;
  onImported: (source: TemplateSource) => void;
  onBusyChange?: (busy: boolean) => void;
  disabled?: boolean;
}) {
  const [source, setSource] = useState<TemplateSource | null>(null),
    [html, setHtml] = useState(""),
    [text, setText] = useState<string | null>(null),
    [reason, setReason] = useState("");
  const [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [loaded, setLoaded] = useState(false),
    [imported, setImported] = useState(false);
  const generation = useRef(0);
  const [reload, setReload] = useState(0), [stale, setStale] = useState(false);
  useEffect(() => { onBusyChange?.(busy); }, [busy, onBusyChange]);
  useEffect(() => {
    const request = ++generation.current;
    const current = () => generation.current === request;
    setLoaded(false);
    setBusy(false);
    setStale(false);
    setReason("");
    setError("");
    setSource(null);
    setHtml("");
    setText(null);
    setImported(false);
    inspectTemplate(templateId)
      .then((value) => {
        if (current()) {
          setSource(value);
          setHtml(value.html);
          setText(value.text);
          setLoaded(true);
        }
      })
      .catch((failure) => {
        if (current()) {
          const problem = apiProblem(failure);
          setError(sourceProblem(problem.code));
          setLoaded(problem.code === "template_source_required");
        }
      });
    return () => {
      generation.current++;
    };
  }, [templateId, reload]);
  const save = async () => {
    const request = generation.current;
    setBusy(true);
    setError("");
    try {
      const value = await importTemplate(
        templateId,
        html,
        text,
        source?.current_version || 0,
        reason,
      );
      if (generation.current !== request) return;
      setSource(value);
      setImported(true);
      onImported(value);
    } catch (failure) {
      if (generation.current !== request) return;
      const problem = apiProblem(failure);
      setError(sourceProblem(problem.code));
      setStale(problem.code === "template_version_changed");
    } finally {
      if (generation.current === request) setBusy(false);
    }
  };
  const readFile = async (file: File | undefined, field: "HTML" | "Plain text") => {
    if (!file) return;
    if (file.size > 2097152) {
      setError(`${field} must be no larger than 2 MiB.`);
      return;
    }
    const request = generation.current;
    setBusy(true);
    setError("");
    try {
      const value = new TextDecoder("utf-8", { fatal: true, ignoreBOM: true }).decode(await file.arrayBuffer());
      if (generation.current === request) (field === "HTML" ? setHtml : setText)(value);
    } catch {
      if (generation.current === request) setError(`Could not read ${field} file as UTF-8. Choose a valid UTF-8 file.`);
    } finally {
      if (generation.current === request) setBusy(false);
    }
  };
  return (
    <section className="wf-source wf-source-editor">
      <h4>Imported source: {templateId}</h4>
      <p>
        The complete imported HTML is the content sent after approval. The
        original provider template ID is provenance; it does not prove the
        remote body matches.
      </p>
      {error && <p className="wf-warning" role="alert">{error}</p>}
      {error && <button type="button" disabled={busy || disabled} onClick={() => setReload(value => value + 1)}>Reload source</button>}
      {!loaded && !error && <p>Loading source…</p>}
      {source && (
        <>
          <p>
            Source version {source.version} ·{" "}
            {source.provider_metadata
              ? "Provider metadata captured"
              : "Remote drift untracked"}
          </p>
          <p className="wf-hash">Source hash: {source.source_hash}</p>
          <p>Required variables: {source.tokens.join(", ") || "None"}</p>
        </>
      )}
      {loaded && (
        <fieldset className="wf-controls" disabled={busy || disabled}>
          <label>
            Load HTML file
            <input
              type="file"
              accept=".html,.htm,text/html"
              onChange={(e) => { void readFile(e.target.files?.[0], "HTML"); }}
            />
          </label>
          <label>
            Template HTML
            <textarea
              value={html}
              rows={10}
              onChange={(e) => setHtml(e.target.value)}
            />
          </label>
          <label>
            Load optional plain text file
            <input type="file" accept=".txt,text/plain" onChange={(e) => { void readFile(e.target.files?.[0], "Plain text"); }} />
          </label>
          <label>
            Optional plain text
            <textarea
              value={text ?? ""}
              rows={4}
              onChange={(e) => setText(e.target.value || null)}
            />
          </label>
          <label>
            Source change reason
            <input
              value={reason}
              maxLength={2000}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <button
            className="wf-source-import"
            type="button"
            disabled={busy || stale || !html.trim() || !reason.trim()}
            onClick={save}
          >
            Import new source version
          </button>
          <details>
            <summary>Inspect pasted HTML safely before import</summary>
            <p>
              Scripts, images, styling and links are disabled. Variables are
              unresolved here; use workflow preview for rendering.
            </p>
            <SafeEmailPreview html={html} title="Imported source inspection" />
          </details>
        </fieldset>
      )}
      {imported && (
        <p role="status">
          The new source requires a new preview and publication, then explicit
          pattern rebinding. Existing published content is not upgraded.
        </p>
      )}
    </section>
  );
}
